import { Controller } from '@hotwired/stimulus'
import $ from 'jquery'
import 'bootstrap-datepicker'
import {
  generatePresignedUrl,
  uploadFileInForm,
  areAllFilesUploaded,
} from '../../modules/s3_uploader'
import { loadPhoneInput, validateInput } from '../../modules/phone_input'
import { runPrefill } from '../../../shared/registration_doc_prefill'

// Combined SG KYC form (director + company on one page, one submit). Tolerant of
// the fields the SG forms omit (no ID / registration uploads, no birthyear toggle,
// locked MyInfo fields) -- every target access is guarded.
export default class extends Controller {
  static targets = [
    'phoneInput',
    'validMsg',
    'errorMsg',
    'kycForm',
    'businessProfileInput',
    'stepBasic',
    'stepDocs',
    'parseStatus',
  ]

  static values = {
    parseUrl: String,
    documentName: { type: String, default: 'ACRA Business Profile' },
    parseMessages: Object,
  }

  // Step 1 -> Step 2 (manual path): validate the basic-info fields, then reveal
  // the document-upload step.
  nextStep(e) {
    e.preventDefault()
    if (!this._validateInput()) return
    const fields = this.stepBasicTarget.querySelectorAll('input, select, textarea')
    for (const field of fields) {
      if (!field.checkValidity()) {
        field.reportValidity()
        return
      }
    }
    this.stepBasicTarget.classList.add('d-none')
    this.stepDocsTarget.classList.remove('d-none')
    window.scrollTo({ top: 0 })
  }

  prevStep(e) {
    e.preventDefault()
    this.stepDocsTarget.classList.add('d-none')
    this.stepBasicTarget.classList.remove('d-none')
    window.scrollTo({ top: 0 })
  }

  connect() {
    window.addEventListener('load', () => this._loadPhoneInput())

    this._loadPhoneInput()
    this._handleFileInputs()

    // Global selector covers both the director birthday and company date fields.
    $('.datepicker').datepicker({
      format: 'dd/mm/yyyy',
      autoclose: true,
    })

    if (this.hasKycFormTarget) {
      this.kycFormTarget.addEventListener('submit', this.submitForm.bind(this))
    }
  }

  _loadPhoneInput() {
    if (this.iti) return
    if (!this.hasPhoneInputTarget) return
    this.iti = loadPhoneInput(
      this.phoneInputTarget,
      this.data.get('country'),
      this.errorMsgTarget,
      this.validMsgTarget
    )
  }

  _handleFileInputs() {
    // S3 folder per document; unknown inputs fall back to a generic folder.
    // The company form fields are prefixed ("company-...") to avoid colliding
    // with the director form's `email`/`phone_number` field names.
    const folders = {
      front_id_file: 'director_kycs/',
      back_id_file: 'director_kycs/',
      'company-business_registration_file': 'business_registrations/',
      'company-business_profile_file': 'business_profiles/',
    }

    document.querySelectorAll('input[type=file]').forEach((fileInput) => {
      const folder = folders[fileInput.name] || 'kyc_uploads/'

      // The ACRA profile is read as soon as it is chosen so the company fields
      // below can be filled in; it uploads itself on the way.
      if (fileInput.name === 'company-business_registration_file' && this.parseUrlValue) {
        fileInput.addEventListener('change', (e) => this._prefillFromDocument(e.target))
      } else {
        fileInput.addEventListener('change', (e) => generatePresignedUrl(folder, e.target))
      }

      fileInput.addEventListener('change', function () {
        const selectedFile = fileInput.files[0]
        if (!selectedFile) return
        const maxSize = 25 * 1024 * 1024 // 25MB
        if (selectedFile.size >= maxSize) {
          alert(
            'File size exceeds the maximum allowed size of 25MB. Please select a smaller file.',
          )
          fileInput.value = ''
        }
      })
    })
  }

  _prefillFromDocument(input) {
    runPrefill({
      input,
      form: this.kycFormTarget,
      parseUrl: this.parseUrlValue,
      statusEl: this.hasParseStatusTarget ? this.parseStatusTarget : null,
      documentName: this.documentNameValue,
      dateFormat: 'dmy',
      messages: this.parseMessagesValue,
      // The company form is prefixed so its `email`/`phone_number` don't collide
      // with the director form's.
      fieldPrefix: 'company-',
    })
  }

  _validateInput() {
    if (!this.hasPhoneInputTarget) return true
    return validateInput(
      this.iti,
      this.phoneInputTarget,
      this.errorMsgTarget,
      this.validMsgTarget
    )
  }

  submitForm(e) {
    const form = this.kycFormTarget

    if (!this._validateInput()) {
      e.preventDefault()
      return
    }

    if (areAllFilesUploaded(form)) {
      return
    }

    e.preventDefault()
    form.submit.disabled = true

    uploadFileInForm(form, () => {
      form.submit.disabled = false
      form.submit.click()
    })
  }
}
